/**
 * Mermaid / C4-PlantUML `C4Context|C4Container|C4Component` → DiagramData
 * importer (pure, unit-tested).
 *
 * Supports the common C4 builder calls (the text spec shared by Mermaid C4 and
 * C4-PlantUML — NOT the GPL PlantUML engine):
 *   Person(id, "label", "desc")            Person_Ext(...)
 *   System(id, "label", "desc")            System_Ext(...)  SystemDb(...)
 *   Container(id, "label", "tech", "desc")  ContainerDb(...) ContainerQueue(...)
 *   Component(id, "label", "tech", "desc")
 *   Rel(from, to, "label", "tech"?)         BiRel(...)  Rel_U/D/L/R(...)
 *   *_Boundary(id, "label") { ... }         — flattened with a warning (B2/ELK).
 *
 * Elements carry `c4Content`; relationships are ordinary labelled DiagramEdges.
 */

import type { DiagramData, DiagramNode, DiagramEdge, C4Content } from '../types/schema'

export interface MermaidC4ImportResult {
  data: DiagramData
  warnings: string[]
}

interface ParsedC4 {
  id: string
  content: C4Content
}

/** Split a builder call's arg list, respecting double-quoted segments. */
function splitArgs(inner: string): string[] {
  const args: string[] = []
  let cur = ''
  let inQ = false
  for (const ch of inner) {
    if (ch === '"') { inQ = !inQ; continue }
    if (ch === ',' && !inQ) { args.push(cur.trim()); cur = ''; continue }
    cur += ch
  }
  if (cur.trim()) args.push(cur.trim())
  return args
}

/** Builder name → element type + whether tech is a positional arg + external. */
function classifyBuilder(name: string): { type: C4Content['elementType']; hasTech: boolean; external: boolean } | null {
  const external = /_Ext$/.test(name)
  const base = name.replace(/_Ext$/, '')
  if (base === 'Person') return { type: 'person', hasTech: false, external }
  if (base === 'System' || base === 'SystemDb' || base === 'SystemQueue') return { type: 'system', hasTech: false, external }
  if (base === 'Container' || base === 'ContainerDb' || base === 'ContainerQueue') return { type: 'container', hasTech: true, external }
  if (base === 'Component' || base === 'ComponentDb' || base === 'ComponentQueue') return { type: 'component', hasTech: true, external }
  return null
}

export function parseMermaidC4Diagram(input: string): MermaidC4ImportResult {
  const warnings: string[] = []
  const elements = new Map<string, ParsedC4>()
  const edges: DiagramEdge[] = []
  let sawHeader = false
  let boundaryDepth = 0

  const lines = input.split('\n').map(l => l.trim()).filter(Boolean)
  for (const line of lines) {
    if (line.startsWith('%%') || line.startsWith("'")) continue
    if (/^C4(Context|Container|Component|Dynamic|Deployment)\b/.test(line)) { sawHeader = true; continue }
    if (/^(title|UpdateLayoutConfig|UpdateElementStyle|UpdateRelStyle|SHOW_LEGEND)\b/.test(line)) continue

    // Boundary open / close — flatten contents (nesting awaits ELK / B2).
    if (/_Boundary\s*\(/.test(line) && line.includes('{')) {
      boundaryDepth++
      if (boundaryDepth === 1) warnings.push('C4 boundary flattened (nested layout pending ELK support).')
      continue
    }
    if (line === '}') { if (boundaryDepth > 0) boundaryDepth--; continue }

    const call = /^(\w+)\s*\((.*)\)\s*\{?\s*$/.exec(line)
    if (!call) { warnings.push(`Skipped unrecognised line: ${line}`); continue }
    const name = call[1]
    const args = splitArgs(call[2])

    // Relationships
    if (/^(BiRel|Rel)(_[UDLRB][a-z]*)?$/.test(name) || /^Rel_Back$/.test(name)) {
      const [from, to, label, tech] = args
      if (!from || !to) { warnings.push(`Skipped relation (need from,to): ${line}`); continue }
      edges.push({
        id: `e-${from}-${to}-${edges.length}`,
        source: from,
        target: to,
        label: tech ? `${label ?? ''} [${tech}]`.trim() : label,
        edgeType: 'relationship',
        type: 'custom',
        data: { arrowEnd: 'arrow', ...(name.startsWith('BiRel') ? { bidirectional: true, arrowStart: 'arrow' } : {}) },
      })
      continue
    }

    const cls = classifyBuilder(name)
    if (!cls) { warnings.push(`Skipped unrecognised builder: ${name}`); continue }
    const id = args[0]
    if (!id) continue
    const label = args[1] ?? id
    const technology = cls.hasTech ? args[2] : undefined
    const description = cls.hasTech ? args[3] : args[2]
    elements.set(id, {
      id,
      content: { elementType: cls.type, name: label, technology: technology || undefined, description: description || undefined, external: cls.external || undefined },
    })
  }

  if (!sawHeader) warnings.push('No `C4Context/Container/Component` header found — parsed best-effort.')

  const nodes: DiagramNode[] = []
  let idx = 0
  const COLS = 3
  for (const el of elements.values()) {
    nodes.push({
      id: el.id,
      label: el.content.name,
      nodeType: el.content.elementType === 'person' ? 'actor' : 'component',
      position: { x: (idx % COLS) * 260, y: Math.floor(idx / COLS) * 200 },
      c4Content: el.content,
    })
    idx++
  }

  const data: DiagramData = {
    diagramType: 'c4',
    title: 'C4 Diagram',
    nodes,
    edges,
    layout: { algorithm: 'dagre', direction: 'TB' },
  }
  return { data, warnings }
}
